import React, { useEffect, useState } from 'react';
import { useMutation } from '@apollo/client';
import { useSelector, useDispatch } from 'react-redux';
import { Button } from '@BICE_Vida/components';
import classnames from 'classnames';

import ModalNoUsefulReason from './ModalNoUsefulReason';
import SubmitModal from '../SubmitModal/SubmitModal';
import { Container, Row, Col } from '../../designSystem/Container/Container';
import Text from '../../designSystem/Text';

import styles from './UsefulQuestion.module.scss';

import { useFulQuestionText, feedbackThanksText } from '../../utils/generalTexts';

import * as feedbackSelector from '../../redux/feedback/selectors';
import * as feedbackActions from '../../redux/feedback/actions';

import createFeedback from '../../graphQL/mutations/createFeedback';

const UsefulQuestion = ({ question }: UsefulQuestionType) => {
  const dispatch = useDispatch();
  const [createQuestionFeedback] = useMutation(createFeedback);
  const [openModalNoUseful, setOpenModalNoUseful] = useState(false);
  const [openSubmitModal, setVisibleSubmitModal] = useState(false);
  const handlerSubmitModal = (tryOpen: boolean) => setVisibleSubmitModal(tryOpen);

  const thereIsFeedback = useSelector(
    feedbackSelector.getFeedback(question.categoryId, question.questionId)
  );

  const btnYesClass = classnames(styles.button_yes, {
    [styles.btn_with_feedback]: thereIsFeedback?.useful,
  });

  const btnNoClass = classnames(styles.button_no, {
    [styles.btn_with_feedback]: thereIsFeedback?.useful,
  });

  const [feedbackToPersist, setUserFeedback] = useState<UserFeedBack>({
    questionId: '',
    questionTitle: '',
    categoryId: '',
    useful: false,
    reason: {
      reasonId: '',
      reasonDescription: '',
    },
  });

  const sendDataSave = async () => {
    try {
      const data = await createQuestionFeedback({
        variables: {
          input: {
            categoryId: feedbackToPersist.categoryId,
            questionId: feedbackToPersist.questionId,
            questionTitle: feedbackToPersist.questionTitle,
            answer: feedbackToPersist.useful,
            reason: {
              reasonId: feedbackToPersist.reason?.reasonId,
              reasonDescription: feedbackToPersist.reason?.reasonDescription,
            },
          },
        },
      });

      return data;
    } catch (error) {
      return error;
    }
  };

  useEffect(() => {
    if (feedbackToPersist.questionId.length > 0) {
      sendDataSave();
      dispatch(
        feedbackActions.setQuestionFeedback({
          ...question,
          useful: feedbackToPersist.useful,
        })
      );
    }
  }, [feedbackToPersist]);

  const saveFeedback = async (positiveFeedback: boolean, incomingReason?: ReasonFeedback) => {
    if (!positiveFeedback && incomingReason !== undefined) {
      setUserFeedback({
        ...question,
        useful: positiveFeedback,
        reason: {
          reasonId: incomingReason.reasonId,
          reasonDescription: incomingReason.reasonDescription,
        },
      });
      handlerReasonModal(false);
      handlerSubmitModal(true);
    }

    if (positiveFeedback) {
      setUserFeedback({
        ...question,
        useful: positiveFeedback,
      });
    }
  };

  const handlerReasonModal = (tryOpen: boolean) => {
    if (thereIsFeedback?.useful === false && tryOpen) {
      return;
    }
    setOpenModalNoUseful(tryOpen);
  };

  const handlerUsefulQuestion = () => {
    if (thereIsFeedback?.useful) {
      return;
    }
    saveFeedback(true);
  };

  const handlerIncommingReason = (reason: ReasonFeedback) => saveFeedback(false, reason);

  return (
    <Container className={styles.useful_container}>
      <Row justify="center" align="center" className={styles.row_useful}>
        <Col className={styles.col_title}>
          <Text customClass={styles.text_title} text={useFulQuestionText.title} />
        </Col>
        <Col className={styles.col_button}>
          <Button
            size="medium"
            variant="text"
            text={useFulQuestionText.usefulYes}
            className={btnYesClass}
            onClick={handlerUsefulQuestion}
          />
          <Button
            size="medium"
            variant="text"
            text={useFulQuestionText.usefulNo}
            className={btnNoClass}
            onClick={() => handlerReasonModal(true)}
          />
        </Col>
      </Row>
      <ModalNoUsefulReason
        isOpen={openModalNoUseful}
        closeModal={() => handlerReasonModal(false)}
        sendReason={handlerIncommingReason}
      />
      <SubmitModal
        isOpen={openSubmitModal}
        closeModal={() => handlerSubmitModal(false)}
        textContent={feedbackThanksText}
        modalActionBehavior={() => handlerSubmitModal(false)}
      />
    </Container>
  );
};

type UsefulQuestionType = {
  question: Question;
};

export default UsefulQuestion;
